import { ShippingMethodTypes } from "@features/checkout/types";
import { activeSiteConfig } from "@config/site/site-config";
import type { Cart, LineItem, MyCartUpdateAction } from "@graphql/generated/types";
import { executeCommerceToolsWithRetry } from "@lib/utils/executeCommerceToolsWithRetry";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import {
    useCreateMyCart,
    useRecalculateCart,
    useRestoreProductToCart,
} from "@lib/commerce/hooks/cartOperations";
import { useUpdateCart } from "@lib/commerce/hooks/checkoutOperations";
import { buildSupplyChannelActions } from "@lib/utils/checkout-utility/supplyChannelActions";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import {
    useAddProductToCart,
    useRemoveProductFromCart,
    useUpdateProductQtyInCart,
} from "@lib/commerce/hooks/productOperations";
import { useStore } from "@store/store";
import { useLocale } from "next-intl";
import { isCartNotFoundError } from "@lib/utils/graphql/isCartNotFoundError";
import { singleFlight } from "@lib/utils/singleFlight";
import { GraphQLError } from "graphql";
import { useCallback } from "react";

type RecalculateCartArgs = {
    cartId: string;
    cartVersion: number;
    storeKey: string;
    locale: string;
    execute: ReturnType<typeof useRecalculateCart>;
};

// Module scope on purpose: shared by every useCart() instance, so
// simultaneous callers (checkout init, app-wide fresh-data refresh) wait
// on one recalculation per cart instead of each sending a stale version
// and hitting ConcurrentModification.
const recalculateCartSingleFlight = singleFlight(
    ({ cartId, cartVersion, storeKey, locale, execute }: RecalculateCartArgs) =>
        executeCommerceToolsWithRetry(
            execute,
            { cartId, cartVersion, storeKey, locale },
            0,
            "Executing cart recalculation"
        ),
    ({ cartId }) => cartId
);

type CreateCartArgs = Parameters<ReturnType<typeof useCreateMyCart>>[0] & {
    execute: ReturnType<typeof useCreateMyCart>;
};

// Module scope so the PDP's eager cart creation and a concurrent add-to-bag share one cart.
const createCartSingleFlight = singleFlight(
    ({ execute, ...body }: CreateCartArgs) => execute(body),
    () => "my-cart"
);

export const useCart = () => {
    const executeAddProductToCart = useAddProductToCart();
    const executeRestoreProductToCart = useRestoreProductToCart();
    const executeUpdateQtyInCart = useUpdateProductQtyInCart();
    const executeUpdateCart = useUpdateCart();
    const executeCreateMyCart = useCreateMyCart();
    const executeRemoveProductFromCart = useRemoveProductFromCart();
    const executeRecalculateCart = useRecalculateCart();

    const getCart = useStore((state) => state.getCart);
    const clearCart = useStore((state) => state.clearCart);
    const getStoreData = useStore((state) => state.getStoreData);
    const isStateFieldEnabled = useFeatureFlag("isStateFieldEnabled");
    const isDistrictFieldEnabled = useFeatureFlag("isDistrictFieldEnabled");
    const locale = useLocale();

    const createCart = useCallback(
        async (currency: string): Promise<Cart | null> => {
            return await createCartSingleFlight({
                execute: executeCreateMyCart,
                locale,
                currency,
                preferredShippingMethod: getDeliveryMethodKey(
                    getStoreData()?.storeId
                        ? ShippingMethodTypes.CLICK_AND_COLLECT
                        : ShippingMethodTypes.HOME_DELIVERY
                ),
            });
        },
        [executeCreateMyCart, getStoreData, locale]
    );

    const ensureCart = useCallback(
        async (): Promise<Cart | null> =>
            getCart() ?? (await createCart(activeSiteConfig.currency)),
        [getCart, createCart]
    );

    const performAdd = async (activeCart: Cart, sku: string) => {
        const storeData = getStoreData();
        const supplyChannelId = storeData?.supplyChannel?.[0]?.id;

        const foundItem = activeCart?.lineItems.find((x) => x.variant?.sku === sku);
        if (storeData?.storeId || supplyChannelId) {
            if (!storeData?.storeId || !supplyChannelId || !storeData.countryCode) {
                return {
                    errors: [
                        new GraphQLError(
                            "Selected pickup store is missing its channel or country."
                        ),
                    ],
                };
            }

            const actions: MyCartUpdateAction[] = [
                foundItem
                    ? {
                          changeLineItemQuantity: {
                              lineItemId: foundItem.id,
                              quantity: foundItem.quantity + 1,
                          },
                      }
                    : {
                          addLineItem: {
                              sku,
                              quantity: 1,
                              distributionChannel: {
                                  typeId: "channel",
                                  key: process.env.NEXT_PUBLIC_STORE_KEY,
                              },
                              supplyChannel: { typeId: "channel", id: supplyChannelId },
                          },
                      },
                ...buildSupplyChannelActions(activeCart.lineItems, { id: supplyChannelId }),
            ];

            if (
                activeCart.shippingAddress?.streetName?.trim() ||
                activeCart.shippingAddress?.city?.trim() ||
                activeCart.shippingAddress?.postalCode?.trim()
            ) {
                const [
                    { buildShippingAddressAction, buildShippingMethodAction },
                    { mapClickAndCollectStoreToDeliveryAddress },
                    { sanitizeRegionalAddressFields },
                ] = await Promise.all([
                    import("@lib/utils/checkout-utility/deliveryStepActions"),
                    import("@features/checkout/components/delivery-step/utils/mapPickupPointToAddress"),
                    import("@lib/utils/checkoutHelper"),
                ]);
                const pickupAddress = sanitizeRegionalAddressFields(
                    mapClickAndCollectStoreToDeliveryAddress(storeData),
                    { isStateFieldEnabled, isDistrictFieldEnabled }
                );
                const customFields = (activeCart.shippingAddress.custom?.customFieldsRaw ?? [])
                    .filter(
                        (field) =>
                            ![
                                "address_CF_ParcelShopId",
                                "address_CF_ParcelShopCarrier",
                                "address_CF_ParcelTypeCode",
                            ].includes(field.name)
                    )
                    .map((field) => ({ name: field.name, value: JSON.stringify(field.value) }));

                actions.push(
                    buildShippingAddressAction(
                        activeCart.shippingAddress,
                        { ...pickupAddress, salutation: activeCart.shippingAddress.salutation },
                        customFields
                    ),
                    buildShippingMethodAction(
                        getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT)
                    )
                );
            }

            const { errors } = await executeCommerceToolsWithRetry(
                executeUpdateCart,
                {
                    cartId: activeCart.id,
                    cartVersion: activeCart.version,
                    storeKey: activeCart.store.key,
                    locale,
                    actions,
                },
                0,
                "Adding product and synchronizing pickup store"
            );
            return { errors };
        }

        // Item already exists in basket. Update the quantity.
        if (foundItem) {
            const { errors } = await executeCommerceToolsWithRetry(
                executeUpdateQtyInCart,
                {
                    lineItemId: foundItem.id,
                    quantity: foundItem.quantity + 1,
                    cartId: activeCart.id,
                    cartVersion: activeCart.version,
                    storeKey: activeCart.store.key,
                    locale: locale,
                },
                0,
                "Executing Update Product Quantity in Cart"
            );
            return { errors };
        }

        const { errors } = await executeCommerceToolsWithRetry(
            executeAddProductToCart,
            {
                cartId: activeCart.id,
                cartVersion: activeCart.version,
                storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                sku: sku,
                quantity: 1,
                distributionChannel: process.env.NEXT_PUBLIC_STORE_KEY,
                locale,
            },
            0,
            "Executing Add Product to Cart"
        );

        return { errors };
    };

    const addToCart = async (sku: string) => {
        if (!sku) {
            return { errors: [new GraphQLError("No SKU provided.")] };
        }

        const { currency } = activeSiteConfig;
        let activeCart = getCart();
        if (!activeCart) {
            activeCart = await createCart(currency);
        }
        if (!activeCart) {
            return { errors: [new GraphQLError("Failed to create cart.")] };
        }

        const result = await performAdd(activeCart, sku);

        // The session referenced a cart that no longer exists in the store
        // (expired, deleted, or store mismatch). Provision a fresh cart and retry once.
        if (isCartNotFoundError(result.errors)) {
            clearCart();
            const freshCart = await createCart(currency);
            if (!freshCart) {
                return result;
            }
            return performAdd(freshCart, sku);
        }

        return result;
    };

    const removeFromCart = async (lineItemId: string) => {
        if (!lineItemId) {
            return { errors: [new GraphQLError("No lineItemId provided.")] };
        }
        const activeCart = getCart();
        if (!activeCart) {
            return { errors: [new GraphQLError("No active cart found.")] };
        }

        const item = activeCart.lineItems.find((x) => x.id === lineItemId);
        if (!item) {
            return { errors: [new GraphQLError("Item not found in cart.")] };
        }
        const { errors } = await executeCommerceToolsWithRetry(
            executeRemoveProductFromCart,
            {
                lineItemId: item.id,
                quantity: item.quantity,
                cartId: activeCart.id,
                cartVersion: activeCart.version,
                storeKey: activeCart.store.key,
                locale,
            },
            0,
            "Executing Remove Product from Cart"
        );

        return {
            errors,
            data: {
                item: item,
                cart: activeCart,
            },
        };
    };

    const restoreToCart = async (item: LineItem) => {
        const sku = item.variant?.sku;
        if (!sku) {
            return { errors: [new GraphQLError("No SKU provided.")] };
        }

        const activeCart = getCart();
        if (!activeCart) {
            return { errors: [new GraphQLError("No active cart found.")] };
        }

        const existingItem = activeCart.lineItems.find((lineItem) => lineItem.variant?.sku === sku);
        const supplyChannelId = item.supplyChannel?.id;

        if (existingItem) {
            const { errors } = await executeCommerceToolsWithRetry(
                executeUpdateQtyInCart,
                {
                    lineItemId: existingItem.id,
                    quantity: existingItem.quantity + item.quantity,
                    cartId: activeCart.id,
                    cartVersion: activeCart.version,
                    storeKey: activeCart.store.key,
                    locale,
                },
                0,
                "Restoring removed product quantity in cart"
            );

            return { errors };
        }

        if (!item.addedAt) {
            return { errors: [new GraphQLError("No addedAt on removed line item.")] };
        }

        const { errors } = await executeCommerceToolsWithRetry(
            executeRestoreProductToCart,
            {
                cartId: activeCart.id,
                cartVersion: activeCart.version,
                storeKey: activeCart.store.key,
                sku,
                quantity: item.quantity,
                distributionChannel: process.env.NEXT_PUBLIC_STORE_KEY,
                ...(supplyChannelId && {
                    supplyChannel: { typeId: "channel", id: supplyChannelId },
                }),
                locale,
                addedAt: item.addedAt,
            },
            0,
            "Restoring removed product to cart"
        );

        return { errors };
    };

    const recalculateCart = async () => {
        const cart = getCart();
        if (!cart) {
            return { errors: [new GraphQLError("No active cart found.")] };
        }
        if (!cart.lineItems?.length) return { errors: [new GraphQLError("No items in cart.")] };

        const { errors } = await recalculateCartSingleFlight({
            cartId: cart.id,
            cartVersion: cart.version,
            storeKey: cart.store.key,
            locale,
            execute: executeRecalculateCart,
        });

        return { errors };
    };

    return {
        addToCart,
        removeFromCart,
        restoreToCart,
        recalculateCart,
        ensureCart,
    };
};
